import { dotMarkingGroupFor, requiresHazardLabel } from "./dotMarkingGroup";
import { PictogramKey } from "./dotPictograms";
import { diamondForHazardClass } from "./hazardClassDiamond";
import { hazardCharacteristics } from "./hazardousWasteLabelVisuals";
import { listedWasteHazardsFor } from "./listedWasteHazards";

/**
 * The four RCRA hazards 40 CFR 262.16(b)(6)(i)(B) and 262.17(a)(5)(i)(B) ask
 * a label to indicate. Naming the word is not enough on its own -- the label
 * must show a symbol that corresponds, which is what this module resolves.
 */
export type RcraCharacteristic = "Ignitable" | "Corrosive" | "Reactive" | "Toxic";

/** Printed in this order whatever order the inputs arrive in. */
const ORDER: RcraCharacteristic[] = ["Ignitable", "Corrosive", "Reactive", "Toxic"];

const PICTOGRAM_BY_CHARACTERISTIC: Record<RcraCharacteristic, PictogramKey> = {
  Ignitable: "FLAME",
  Corrosive: "CORROSION",
  Reactive: "EXPLODING_BOMB",
  Toxic: "SKULL",
};

/**
 * Where a characteristic can be read from: the profile booleans, the federal
 * D-code characteristics, and the 40 CFR 261.31-.33 listed-code table, unioned
 * together rather than one overriding another. Details: docs/waste/dot-label-rendering.md.
 */
export interface RcraCharacteristicSource {
  federalWasteCodes: string[];
  profileIsIgnitable?: boolean | null;
  profileIsCorrosive?: boolean | null;
  profileIsReactive?: boolean | null;
  profileIsToxic?: boolean | null;
}

export function rcraCharacteristicsFor(source: RcraCharacteristicSource): RcraCharacteristic[] {
  const named = new Set<string>(hazardCharacteristics(source.federalWasteCodes));
  for (const rawCode of source.federalWasteCodes) {
    for (const characteristic of listedWasteHazardsFor(rawCode)) {
      named.add(characteristic);
    }
  }
  if (source.profileIsIgnitable) named.add("Ignitable");
  if (source.profileIsCorrosive) named.add("Corrosive");
  if (source.profileIsReactive) named.add("Reactive");
  if (source.profileIsToxic) named.add("Toxic");
  return ORDER.filter((characteristic) => named.has(characteristic));
}

/** The pictograms the DOT labels on this label already show. */
function dotCoveredPictograms(
  dotHazardClass: string | null | undefined,
  dotSubsidiaryHazardClass: string | null | undefined
): Set<PictogramKey> {
  const covered = new Set<PictogramKey>();
  for (const hazardClass of [dotHazardClass, dotSubsidiaryHazardClass]) {
    if (!hazardClass) continue;
    const pictogram = diamondForHazardClass(hazardClass)?.design.pictogram;
    if (pictogram) covered.add(pictogram);
  }
  return covered;
}

/**
 * The RCRA symbols the label draws, after subtracting the ones the DOT
 * labels already draw.
 *
 * The compliance review recommended the DOT pictogram "hit two birds with one
 * stone". That holds per SYMBOL, not per label: a waste that is both
 * ignitable and corrosive but ships as Class 3 gets a flame from DOT and
 * nothing for corrosivity, so the corrosion symbol is still owed. Subtracting
 * rather than always-drawing keeps a Class 3 ignitable-only drum from
 * carrying two identical flames.
 */
export function rcraPictogramsFor(args: {
  characteristics: RcraCharacteristic[];
  dotHazardClass?: string | null;
  dotSubsidiaryHazardClass?: string | null;
}): Array<{ characteristic: RcraCharacteristic; pictogram: PictogramKey }> {
  const covered = dotCoveredPictograms(args.dotHazardClass, args.dotSubsidiaryHazardClass);
  return ORDER.filter((characteristic) => args.characteristics.includes(characteristic))
    .map((characteristic) => ({
      characteristic,
      pictogram: PICTOGRAM_BY_CHARACTERISTIC[characteristic],
    }))
    .filter(({ pictogram }) => !covered.has(pictogram));
}

/** What the label's Hazards row reads, across both renderers. */
export interface LabelPictogramSource extends RcraCharacteristicSource {
  containerType?: string | null;
  capacity?: number | null;
  capacityUnit?: string | null;
  physicalState?: string | null;
  dotHazardClass?: string | null;
  dotSubsidiaryHazardClass?: string | null;
}

/**
 * The RCRA symbols this label's Hazards row prints.
 *
 * One function rather than one per surface: the PDF, Oak's preview and the
 * PDF's layout estimator all need this answer, and an estimator that worked
 * it out separately would disagree with the renderer the next time the rule
 * moved -- which is how the row came to be drawn without any height budgeted
 * for it.
 *
 * A DOT class only covers a characteristic on a packaging that prints its
 * hazard label. A placarded bulk container's extra page is the ID number
 * alone, so its class is not subtracted: doing so takes the flame off an
 * ignitable tank car and puts nothing back.
 */
export function labelRcraPictograms(
  source: LabelPictogramSource
): Array<{ characteristic: RcraCharacteristic; pictogram: PictogramKey }> {
  // The whole source, not just the type code: how much the container holds is
  // what decides bulk (49 CFR 171.8), and a bulk packaging prints no hazard
  // label to subtract.
  const container = source.containerType
    ? {
        containerType: source.containerType,
        capacity: source.capacity,
        capacityUnit: source.capacityUnit,
        physicalState: source.physicalState,
      }
    : undefined;
  const group = container ? dotMarkingGroupFor(container) : undefined;
  const labelsPrint =
    group !== undefined && container !== undefined && requiresHazardLabel(group, container);
  return rcraPictogramsFor({
    characteristics: rcraCharacteristicsFor(source),
    dotHazardClass: labelsPrint ? source.dotHazardClass : undefined,
    dotSubsidiaryHazardClass: labelsPrint ? source.dotSubsidiaryHazardClass : undefined,
  });
}
